import { useForm } from "@mantine/form"
import { httpsCallable } from "firebase/functions"

import { useCloudFunction } from "../../../../hooks/useCloudFunction"
import { AwsLambdaUpdateThingData } from "../../../../functions/aws/lambda/aws-lambda-updateThing"
import { firebase } from "../../../../services/firebase/firebase"
import { Attribute, DeviceDocument } from "../../../../contexts/devices-context"
import { AwsLambdaUpdateGroupData } from "../../../../functions/aws/lambda/aws-lambda-updateGroup"
import { useLanguage } from "../../../../contexts/language-context"

export default (
    attrs: (Record<"limit_to_days_ago" | "upload_minimum_interval" | "only_upload_wifi", Attribute>) | undefined,
    organisation: string,
    group: string,
    device?: DeviceDocument,
) => {
    const { onStart, onSuccess, onError, processing } = useCloudFunction("aws-lambda-updateThing")
    const { L } = useLanguage()
    const form = useForm({
        initialValues: {
            limitDaysToSync: attrs?.limit_to_days_ago?.value !== null && attrs?.limit_to_days_ago.value !== 0,
            daysToSync: (attrs?.limit_to_days_ago?.value as number | undefined) ?? null,
            onMinimumInterval: attrs?.upload_minimum_interval?.value !== null && attrs?.upload_minimum_interval.value !== 0,
            minimumInterval: (attrs?.upload_minimum_interval?.value as number | undefined) ?? null,
            onlyUploadWifi: attrs?.only_upload_wifi?.value ?? null,
        },
        validate: {
            daysToSync: (value, values) =>
                values.limitDaysToSync && value !== null && value < 1
                    ? L("Days to sync must be a positive number")
                    : null,
            minimumInterval: (value, values) =>
                values.onMinimumInterval && value !== null && value < 1
                    ? L("Minimum interval must be a positive number")
                    : null,
        },
        validateInputOnChange: true,
    })

    const onSubmit = async (values: typeof form.values) => {
        onStart(L("Updating setting..."))

        const module = {
            name: "upload_client",
            attributes: [
                ...((form.isDirty("daysToSync") || form.isDirty("limitDaysToSync")) && attrs?.limit_to_days_ago?.mutable
                    ? [
                        {
                            name: "limit_to_days_ago",
                            value: values.limitDaysToSync ? values.daysToSync : null,
                        },
                    ]
                    : []),

                ...((form.isDirty("onMinimumInterval") || form.isDirty("minimumInterval")) && attrs?.upload_minimum_interval?.mutable
                    ? [
                        {
                            name: "upload_minimum_interval",
                            value: values.onMinimumInterval ? values.minimumInterval : null,
                        },
                    ]
                    : []),
                ...((form.isDirty("onlyUploadWifi")) && attrs?.only_upload_wifi?.mutable
                    ? [
                        {
                            name: "only_upload_wifi",
                            value: values.onlyUploadWifi as boolean ?? null,
                        },
                    ]
                    : []),
            ] as Attribute[],
        }

        try {
            if (device) {
                await httpsCallable<AwsLambdaUpdateThingData>(
                    firebase.functions,
                    "aws-lambda-updateThing",
                )({
                    id: device.id,
                    group,
                    organisation,
                    module,
                })
            } else {
                await httpsCallable<AwsLambdaUpdateGroupData>(
                    firebase.functions,
                    "aws-lambda-updateGroup",
                )({
                    id: group,
                    organisation,
                    module,
                })
            }

            form.resetDirty(values)
            onSuccess()()
        } catch (error) {
            onError()(error)
        }
    }
    return {
        form,
        onSubmit,
        reset: form.reset,
        inheritSettings: () =>
            form.setValues({
                limitDaysToSync: false,
                daysToSync: null,
                onMinimumInterval: false,
                minimumInterval: null,
                onlyUploadWifi: null
            }),
        processing,
    }
}
